import React, { ReactElement, RefObject, useContext, useState } from 'react'

import { Select } from '@material-ui/core'
import { LanguageContext } from '../../Common/Contexts'
import LanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { FieldsList as FieldsListLanguage, LanguagePrompt } from '../../../../i18n'

import Heading from '../../Common/Components/Heading'
import FormRow from '../../Common/Components/FormRow'
import Fieldset from '../../Common/Components/Fieldset'

import Icon, { IconName } from '../../Common/Components/Icon'
import Button from '../../Common/Components/Button'
import { StatisticsConfiguration } from './Types'

import { arrayMove, MovementDirection } from '../../Common/Utils/Array'
import {
    STATISTICS_ORDERING_PRIORITY_MASK,
    STATISTICS_ORDERING_ISDESCENDING,
    STATISTICS_ORDERING_NONE
} from './Constants'

import TabActionPromptContext from '../../Common/Components/Tabs/TabActionPromptContext'
import { RptFilterEntityInfo } from '../../Common/Types/Types'
import { REPORT_STAT_FLAGS_TYPE_NONE } from '../../Common/Constants/ReportStatFlags'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'

// CSS imports. Commented out because of current build process.
// import './fields.scss'

export type FieldsListProps = {
    setShowAddMenu: React.Dispatch<React.SetStateAction<boolean>>
    openSortMenu: () => void
    addListItemPopoverRef: React.RefObject<HTMLDivElement>
    sortPopoverRef: RefObject<HTMLButtonElement>
    setStatsListConfiguredState: React.Dispatch<React.SetStateAction<StatisticsConfiguration[]>>
    statsListConfiguredState: StatisticsConfiguration[]
    handleResetAll: () => void
    selectedStatsIDs: number[]
    setSelectedStatIDs: React.Dispatch<React.SetStateAction<number[]>>
    rptFilterEntityInfo: RptFilterEntityInfo | undefined
    isUserLicensedForCurrentReportStyle: boolean
    isSortingSupportedEver: boolean
    isSortingSupportedRealTime: boolean
    isReportRealTime: boolean
}

const FieldsList: React.FC<FieldsListProps> = ({
    setShowAddMenu,
    openSortMenu,
    addListItemPopoverRef,
    sortPopoverRef,
    setStatsListConfiguredState,
    statsListConfiguredState,
    handleResetAll,
    selectedStatsIDs,
    setSelectedStatIDs,
    rptFilterEntityInfo,
    isUserLicensedForCurrentReportStyle,
    isSortingSupportedEver,
    isSortingSupportedRealTime,
    isReportRealTime
}): ReactElement => {
    // Force the UI to update
    const [, forceUpdate] = useState<object>()

    const language = useContext(LanguageContext) as LanguageHandler
    language.loadBundle('FieldsList')
    language.loadBundle(LanguagePrompt.NAME)
    const { displayPrompt } = useContext(TabActionPromptContext)

    const configuredStatisticsWithSortingCount = statsListConfiguredState.filter(
        (statisticConfiguration) => statisticConfiguration.ordering & STATISTICS_ORDERING_PRIORITY_MASK
    ).length
    const sortIsEnabled = selectedStatsIDs.length === 1
    const removeButtonIsEnabled = selectedStatsIDs.length > 0
    const removeAllButtonIsDisabled = statsListConfiguredState.length === 0

    const isMoveUpButtonDisabled =
        selectedStatsIDs.length === 0 ||
        selectedStatsIDs.some(
            (selectedStatID) =>
                statsListConfiguredState.findIndex(
                    (statisticConfiguration) => statisticConfiguration.ID === selectedStatID
                ) === 0
        )

    const isMoveDownButtonDisabled =
        selectedStatsIDs.length === 0 ||
        selectedStatsIDs.some(
            (selectedStatID) =>
                statsListConfiguredState.findIndex(
                    (statisticConfiguration) => statisticConfiguration.ID === selectedStatID
                ) ===
                statsListConfiguredState.length - 1
        )

    const handleMoveListItems = (movementDirection: MovementDirection): void => {
        if (statsListConfiguredState) {
            const listItems = statsListConfiguredState.filter((stat: StatisticsConfiguration) =>
                selectedStatsIDs.find((ID) => stat.ID === ID)
            )

            const reorderedArray = arrayMove(statsListConfiguredState as [], listItems as [], movementDirection)
            setStatsListConfiguredState(reorderedArray || [])
        }
        // Force update to re-render list.
        forceUpdate({}) // TODO: why is this needed - find a way to avoid necessity to call forceUpdate
    }

    const handleRemoveItemFromList = (): void => {
        const newStatsListConfigured = statsListConfiguredState.filter(
            (item: StatisticsConfiguration) => !selectedStatsIDs.find((ID) => ID === item.ID)
        )

        setStatsListConfiguredState(newStatsListConfigured)
        setSelectedStatIDs([])
    }

    const handleRemoveAll = (): void => {
        displayPrompt(
            `${language?.get(FieldsListLanguage.FieldsList_RemoveAllConfirmMessage)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptCancel)}`,
            () => {
                setStatsListConfiguredState([])
                setSelectedStatIDs([])
            }
        )
    }

    const OnClickResetAll = (): void => {
        displayPrompt(
            `${language?.get(FieldsListLanguage.ResetAllConfirmMessage)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm)}`,
            `${language?.get(LanguagePrompt.LanguagePrompt_PromptCancel)}`,
            () => {
                handleResetAll()
            }
        )
    }

    const handleShowAddItems = (): void => {
        setShowAddMenu(true)
    }

    const onSelectionChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
        const { options } = event.target as HTMLSelectElement
        const value: number[] = []
        for (let i = 0, l = options.length; i < l; i += 1) {
            if (options[i].selected) {
                value.push(Number(options[i].value))
            }
        }
        setSelectedStatIDs(value)
    }

    return (
        <Fieldset className='fields-list-wrapper' popoverRef={addListItemPopoverRef}>
            <Heading level={HeadingLevel.TWO}>{language?.get(FieldsListLanguage.FieldsList_SectionHeader)}</Heading>
            <Select
                className='fields-list'
                native
                value={selectedStatsIDs}
                multiple
                onChange={onSelectionChange}
                disableUnderline
            >
                {statsListConfiguredState?.map((stat: StatisticsConfiguration, i: number) => {
                    let sortingPrefix = ''
                    let alarmingPrefix = ''

                    // Prepare alarming prefix if applicable.
                    const iAlarmComparison = stat.alarmComparisonType
                    if (iAlarmComparison > 0) {
                        alarmingPrefix = language?.get(FieldsListLanguage.FieldsPrefixAlarm)
                    }

                    // Prepare sorting prefix if applicable.
                    const iOrdering = stat.ordering
                    const iOrderingPriority = iOrdering & STATISTICS_ORDERING_PRIORITY_MASK
                    const bIsDescending =
                        (iOrdering & STATISTICS_ORDERING_ISDESCENDING) === STATISTICS_ORDERING_ISDESCENDING

                    if (iOrderingPriority !== STATISTICS_ORDERING_NONE) {
                        let formatString = ''
                        if (configuredStatisticsWithSortingCount > 1) {
                            formatString = bIsDescending
                                ? language?.get(FieldsListLanguage.FieldsPrefixSortDescWithNumberPattern)
                                : language?.get(FieldsListLanguage.FieldsPrefixSortAscWithNumberPattern)
                        } else {
                            formatString = bIsDescending
                                ? language?.get(FieldsListLanguage.FieldsPrefixSortDesc)
                                : language?.get(FieldsListLanguage.FieldsPrefixSortAsc)
                        }

                        sortingPrefix = `(${StringExpandPlaceholders(formatString, [iOrderingPriority.toString()])}) `
                    }

                    // Prepare licensing suffix if applicable.
                    let licensed = false
                    if (
                        isUserLicensedForCurrentReportStyle &&
                        rptFilterEntityInfo &&
                        (Number(rptFilterEntityInfo.EntityInfo.ReportCapabilityInfo.StatFlags) & stat.statFlagsType) >
                            REPORT_STAT_FLAGS_TYPE_NONE
                    ) {
                        licensed = true
                    }

                    const label = `${alarmingPrefix}${sortingPrefix}${stat.mediumName}${
                        licensed ? '' : language.get(FieldsListLanguage.UnlicensedSuffix)
                    }`

                    return (
                        <option className='fields-list-label' key={i} value={stat.ID} title={label}>
                            {label}
                        </option>
                    )
                })}
            </Select>
            <div className='fields-controls'>
                <div className='fields-move'>
                    <Button
                        type='button'
                        onClick={(): void => handleMoveListItems(MovementDirection.UP)}
                        isDisabled={isMoveUpButtonDisabled}
                    >
                        <Icon iconName={IconName.ARROW_UP} />
                        <span className='visually-hidden'>{language?.get(FieldsListLanguage.FieldsList_MoveUp)}</span>
                    </Button>
                    <Button
                        type='button'
                        onClick={(): void => handleMoveListItems(MovementDirection.DOWN)}
                        isDisabled={isMoveDownButtonDisabled}
                    >
                        <Icon iconName={IconName.ARROW_DOWN} />
                        <span className='visually-hidden'>{language?.get(FieldsListLanguage.FieldsList_MoveDown)}</span>
                    </Button>
                </div>
                <div className='fields-add-remove'>
                    {isSortingSupportedEver && (isSortingSupportedRealTime || !isReportRealTime) && (
                        <Button
                            popoverRef={sortPopoverRef}
                            type='button'
                            isDisabled={!sortIsEnabled}
                            onClick={openSortMenu}
                        >
                            <Icon iconName={IconName.SORT} />
                            <span className='visually-hidden'>{language?.get(FieldsListLanguage.Sort)}</span>
                        </Button>
                    )}
                    <Button type='button' onClick={handleRemoveItemFromList} isDisabled={!removeButtonIsEnabled}>
                        <Icon iconName={IconName.REMOVE} />
                        <span className='visually-hidden'>{language?.get(FieldsListLanguage.FieldsList_Remove)}</span>
                    </Button>
                    <Button customClass='fields-add-button' type='button' onClick={handleShowAddItems}>
                        <Icon iconName={IconName.ADD} />
                        <span className='visually-hidden'>{language?.get(FieldsListLanguage.FieldsList_Add)}</span>
                    </Button>
                    <HelpTag helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_FIELDS_ADD} tag={Tags.CONFIGURE_REPORT} />
                </div>
            </div>

            <FormRow isInline className={'actions'}>
                <div className='half-width'>
                    <Button type='button' fullWidth onClick={OnClickResetAll}>
                        {language?.get(FieldsListLanguage.ResetAll)}
                    </Button>
                </div>
                <div className='half-width'>
                    <Button type='button' fullWidth onClick={handleRemoveAll} isDisabled={removeAllButtonIsDisabled}>
                        {language?.get(FieldsListLanguage.FieldsList_RemoveAll)}
                    </Button>
                </div>
            </FormRow>
        </Fieldset>
    )
}

export default FieldsList
